import { useCallback, useState } from 'react';
import { line, DEFAULT_CURRENCY_CODE, LOST_QUOTE_OUTCOMES, Size, fill, formatPercent, releaseOwnerOf, releaseOwnerSentence, t, type LostDealQuote, type LostQuoteOutcome, type SuggestionRow } from '@bms2/shared';
import { Checkbox, Dropdown, NumericInput, Select, TextInput } from '../../components/controls';
import { Money } from '../../components/Money';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { useCompanySearch } from '../settings/api';
import { DEALS_PATH, FILES_PATH, apiFetch, patch, personSuccessorsPath, post, useApi } from './api';
import { NewRecordModal } from './NewRecordModal';
import styles from './RecordPage.module.css';
import targetStyles from './ReleaseTargets.module.css';
import sharedStyles from '../../components/composites/shared.module.css';
import { useAddNew } from './AddNewHost';

/**
 * The Deal page's own modals (piece 19): the Lost dialog that writes the
 * DealLossEvent row with the status change and quick-creates a competitor
 * from inside it (decision 0207); a team member with Commission Share off by
 * default (decision 0114); a contact by employment, the Account's people
 * first; a BOM line; and the release of one of the Deal's files through the
 * gate piece 9 already owns.
 */

interface DealModalProps {
  open: boolean;
  dealId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}

/** 🔒 The Deal's open Quotes, which the Lost save closes Declined or Void (piece 18; T2350). */
export const dealOpenQuotesPath = (dealId: string): string => `${DEALS_PATH}/${encodeURIComponent(dealId)}/open-quotes`;

const LOST_OUTCOME_LABELS: Record<LostQuoteOutcome, string> = { declined: t('deal.lost.outcome.declined'), void: t('deal.lost.outcome.void') };

export function LostDealModal({ open, dealId, apiBaseUrl = '', onCancel, onSaved }: DealModalProps) {
  const reasons = useApi<{ rows: { id: string; name: string; isActive: boolean }[] }>(open ? `${DEALS_PATH}/loss-reasons` : null, apiBaseUrl);
  const openQuotes = useApi<{ rows: LostDealQuote[] }>(open ? dealOpenQuotesPath(dealId) : null, apiBaseUrl);
  // 🔒 Declined is chosen when the dialog opens; a person turns a Quote to Void where the loss is a withdrawal on our side.
  const [outcomes, setOutcomes] = useState<Record<string, LostQuoteOutcome>>({});
  const [reasonId, setReasonId] = useState('');
  const [competitorId, setCompetitorId] = useState<string | null>(null);
  const [competitorName, setCompetitorName] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const search = useCompanySearch(apiBaseUrl);
  const save = async () => {
    setError(null);
    if (!reasonId) return setError(t('deal.lost.needsReason'));
    const quotes = quoteRows.map((row) => ({ quoteId: row.id, outcome: outcomes[row.id] ?? 'declined' }));
    const answer = await post(`${DEALS_PATH}/${dealId}/lost`, { lossReasonId: reasonId, competitorCompanyId: competitorId, detail: detail.trim() || null, quotes }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  const rows = reasons.state.kind === 'ready' ? reasons.state.data.rows.filter((r) => r.isActive) : [];
  const quoteRows = openQuotes.state.kind === 'ready' ? openQuotes.state.data.rows : [];
  // 🔒 A login that writes Deals without reading Quotes is refused the list (T3286): the window says so, and Mark Lost still saves with the Quotes untouched.
  const quotesUnread = openQuotes.state.kind === 'failed';
  return (
    <Modal open={open} title={t('deal.lost.title')} onCancel={onCancel} dataModal="deal-lost" actions={[{ kind: 'primary', label: t('deal.lost.confirm'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <Select size={Size.Medium} label={t('deal.lost.reason')} value={reasonId} onChange={setReasonId} placeholder={t('edit.none')} options={rows.map((r) => ({ value: r.id, label: r.name }))} dataAttribute={{ 'data-deal-field': 'lossReason' }} />
      <div className={styles.field} data-deal-field="competitor">
        <Dropdown chars={32} label={t('deal.lost.competitor')} entityLabel={t('deal.lost.competitorEntity')} search={search} value={competitorName} onSelect={(row: SuggestionRow) => { setCompetitorId(row.id); setCompetitorName(row.line1); }} onAddNew={() => setCreating(true)} />
        <NewRecordModal open={creating} recordType="CompanyRecord" apiBaseUrl={apiBaseUrl} onCancel={() => setCreating(false)} onCreated={(created) => { setCompetitorId(created.id); setCompetitorName(created.label); setCreating(false); }} />
      </div>
      <TextInput chars={32} label={t('deal.lost.detail')} value={detail} onChange={setDetail} name="lostDetail" />
      {quotesUnread ? <p className={styles.lostQuotesHint} data-deal-lost-quotes-unread>{t('deal.lost.quotesUnread')}</p> : null}
      {quoteRows.length > 0 ? (
        <section className={styles.lostQuotes} data-deal-lost-quotes aria-label={t('deal.lost.quotes')}>
          <h3 className={styles.lostQuotesHeading}>{t('deal.lost.quotes')}</h3>
          <p className={styles.lostQuotesHint}>{t('deal.lost.quotesHint')}</p>
          {quoteRows.map((row) => {
            const outcome = outcomes[row.id] ?? 'declined';
            // 🔒 The figure is the Money component inside the sentence (decision 0717): the template splits at its total.
            const [before, after] = t(outcome === 'void' ? 'deal.lost.closesVoid' : 'deal.lost.closesDeclined').split('{total}');
            return (
              <div key={row.id} className={styles.lostQuote} data-lost-quote={row.id}>
                <p className={styles.lostQuoteSentence}>{fill(before ?? '', { number: row.number })}<Money cents={row.totalCents} currencyCode={DEFAULT_CURRENCY_CODE} />{fill(after ?? '', { number: row.number })}</p>
                <Select size={Size.Small} label={fill(t('deal.lost.closeAs'), { number: row.number })} value={outcome} onChange={(value) => setOutcomes((held) => ({ ...held, [row.id]: value as LostQuoteOutcome }))} options={LOST_QUOTE_OUTCOMES.map((value) => ({ value, label: LOST_OUTCOME_LABELS[value] }))} dataAttribute={{ 'data-lost-quote-outcome': row.id }} />
              </div>
            );
          })}
        </section>
      ) : null}
    </Modal>
  );
}

/** Employments by person name. The contact picker asks for the Account's scope and gets its people alone (decision 0328); the team picker searches wider, the Account's own first and marked. */
export function useEmploymentSearch(apiBaseUrl: string, companyId: string | null, withoutTeamOfDealId: string | null = null, scope: 'account' | 'any' | 'staff' = 'any', withoutContactsOfDealId: string | null = null) {
  return useCallback(
    async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      const response = await apiFetch(`${apiBaseUrl}${DEALS_PATH}/employments?q=${encodeURIComponent(query)}${companyId ? `&companyId=${encodeURIComponent(companyId)}` : ''}${withoutTeamOfDealId ? `&withoutTeamOf=${encodeURIComponent(withoutTeamOfDealId)}` : ''}${scope === 'any' ? '' : `&scope=${scope}`}${withoutContactsOfDealId ? `&withoutContactsOf=${encodeURIComponent(withoutContactsOfDealId)}` : ''}`, { signal });
      if (!response.ok) return [];
      const body = (await response.json()) as { rows: { id: string; label: string; personName?: string; companyName: string; atAccount: boolean }[] };
      // 🔒 A staff row reads the person's own company, never the Account's link (T3461).
      return body.rows.map((row) => ({ id: row.id, line1: scope === 'staff' ? row.personName ?? row.label : row.label, line2: scope === 'staff' ? row.companyName : row.atAccount ? t('deal.contact.atAccount') : t('deal.contact.elsewhere') }));
    },
    [apiBaseUrl, companyId, withoutTeamOfDealId, scope, withoutContactsOfDealId],
  );
}

/** 🔒 The people who can take a deleted person's open Deals (T3461): the staff list through the delete's own gate, less the person being deleted. */
export function useSuccessorSearch(apiBaseUrl: string, personId: string) {
  return useCallback(
    async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      const response = await apiFetch(`${apiBaseUrl}${personSuccessorsPath(personId)}?q=${encodeURIComponent(query)}`, { signal });
      if (!response.ok) return [];
      const body = (await response.json()) as { rows: { id: string; label: string; personName?: string; companyName: string }[] };
      return body.rows.map((row) => ({ id: row.id, line1: row.personName ?? row.label, line2: row.companyName }));
    },
    [apiBaseUrl, personId],
  );
}

/**
 * 🔒 Change Owner (decision 10701; T3618): somebody employed here today takes the Deal until it is Won or Lost. The window
 * names the person chosen in its confirming sentence before Save writes through `POST deals/:id/owner`.
 */
export function ChangeOwnerModal({ open, dealId, apiBaseUrl = '', onCancel, onSaved }: DealModalProps) {
  const search = useEmploymentSearch(apiBaseUrl, null, null, 'staff');
  const [employmentId, setEmploymentId] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setError(null);
    if (!employmentId) return setError(t('deal.owner.needsOne'));
    const answer = await post(`${DEALS_PATH}/${dealId}/owner`, { employmentId }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('deal.owner.title')} onCancel={onCancel} dataModal="deal-owner" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <div className={styles.field} data-deal-field="newOwner">
        <Dropdown chars={32} label={t('deal.owner.member')} entityLabel={t('deal.parts.ownerEntity')} search={search} value={name} onSelect={(row) => { setEmploymentId(row.id); setName(row.line1); }} onAddNew={null} />
      </div>
      {name ? <p className={styles.hint} data-deal-owner-confirm={name}>{fill(t('deal.owner.confirm'), { name })}</p> : null}
    </Modal>
  );
}

/** A team row as its pencil loads it; the share is present only where the viewer may see it (decision 0114). */
export interface TeamMemberEdit {
  id: string;
  personName: string;
  roleTypeId: string;
  isPrimary: boolean;
  shareRevealed: boolean;
  commissionSharePercent: number | null;
  /** The share the owner keeps after the open members' explicit shares (decision 0114), drawn on the owner's own row. */
  ownerRemainder: number | null;
}

export function DealTeamModal({ open, dealId, companyId, initial = null, apiBaseUrl = '', onCancel, onSaved }: DealModalProps & { companyId: string | null; initial?: TeamMemberEdit | null }) {
  const addNew = useAddNew(apiBaseUrl);
  const roles = useApi<{ rows: { id: string; name: string; isActive: boolean }[] }>(open ? `${DEALS_PATH}/team-roles` : null, apiBaseUrl);
  const search = useEmploymentSearch(apiBaseUrl, companyId, dealId);
  const [employmentId, setEmploymentId] = useState<string | null>(null);
  const [memberName, setMemberName] = useState<string | null>(initial?.personName ?? null);
  const [roleTypeId, setRoleTypeId] = useState(initial?.roleTypeId ?? '');
  const [shareOn, setShareOn] = useState(initial?.commissionSharePercent !== null && initial?.commissionSharePercent !== undefined);
  const [share, setShare] = useState(initial?.commissionSharePercent === null || initial?.commissionSharePercent === undefined ? '' : String(initial.commissionSharePercent));
  const [error, setError] = useState<string | null>(null);
  // The owner's row holds the remainder and takes no share; a share the viewer cannot see is left as it stands.
  const sharesEditable = !initial || (initial.shareRevealed && !initial.isPrimary);
  const save = async () => {
    setError(null);
    if (!roleTypeId || (!initial && !employmentId)) return setError(t('deal.team.needsBoth'));
    const commissionSharePercent = shareOn && share.trim() !== '' ? Number(share) : null;
    const answer = initial
      ? await patch(`${DEALS_PATH}/${dealId}/team/${initial.id}`, { roleTypeId, ...(sharesEditable ? { commissionSharePercent } : {}) }, apiBaseUrl)
      : await post(`${DEALS_PATH}/${dealId}/team`, { employmentId, roleTypeId, commissionSharePercent }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  const rows = roles.state.kind === 'ready' ? roles.state.data.rows.filter((r) => r.isActive) : [];
  return (
    <Modal open={open} title={initial ? t('deal.team.editTitle') : t('deal.team.title')} onCancel={onCancel} dataModal="deal-team" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <div className={styles.field} data-deal-field="teamMember">
        {initial ? (
          <p className={styles.hint} data-deal-team-member={initial.id}>{memberName}</p>
        ) : (
          <Dropdown chars={32} label={t('deal.team.member')} entityLabel={t('deal.team.entity')} search={search} value={memberName} onSelect={(row) => { setEmploymentId(row.id); setMemberName(row.line1); }} onAddNew={() => addNew.open({ kind: 'employee' }, (row) => { setEmploymentId(row.id); setMemberName(row.line1); })} />
        )}
      </div>
      <Select size={Size.Medium} label={t('deal.team.role')} value={roleTypeId} onChange={setRoleTypeId} placeholder={t('edit.none')} options={rows.map((r) => ({ value: r.id, label: r.name }))} dataAttribute={{ 'data-deal-field': 'teamRole' }} />
      {sharesEditable ? (
        <>
          <Checkbox label={t('deal.team.shareToggle')} checked={shareOn} size={Size.Small} onChange={setShareOn} />
          {shareOn ? <NumericInput label={t('deal.team.share')} kind="percent" value={share.trim() === '' ? null : Number(share)} onChange={(next) => setShare(next === null ? '' : String(next))} name="commissionShare" chars={8} /> : null}
          <p className={styles.hint}>{t('deal.team.shareHint')}</p>
        </>
      ) : initial?.isPrimary ? (
        // 🔨 The owner holds the remainder and carries no share of their own (decision 0114): a split is set from the member's row.
        <p className={styles.hint} data-deal-team-owner-remainder={initial.ownerRemainder === null ? 'unknown' : String(initial.ownerRemainder)}>{line(t('deal.team.ownerRemainder'), initial.ownerRemainder === null ? null : `${formatPercent(initial.ownerRemainder, { maxDecimals: 4 })}.`, t('deal.team.ownerRemainderHint'))}</p>
      ) : (
        <p className={styles.hint} data-deal-team-share-hidden="true">{t('deal.team.shareHidden')}</p>
      )}
      {addNew.host}
    </Modal>
  );
}

export function DealContactModal({ open, dealId, companyId, apiBaseUrl = '', onCancel, onSaved }: DealModalProps & { companyId: string | null }) {
  const addNew = useAddNew(apiBaseUrl);
  // 🔨 The Account's people and nobody else (decision 0328), less those already an open contact on this Deal (T2096).
  const search = useEmploymentSearch(apiBaseUrl, companyId, null, 'account', dealId);
  const [employmentId, setEmploymentId] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setError(null);
    if (!employmentId) return setError(t('deal.contact.needsPerson'));
    const answer = await post(`${DEALS_PATH}/${dealId}/contacts`, { employmentId, isPrimary }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  return (
    <Modal open={open} title={t('deal.contact.title')} onCancel={onCancel} dataModal="deal-contact" actions={[{ kind: 'primary', label: t('modal.save'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <div className={styles.field} data-deal-field="contact">
        <Dropdown chars={32} label={t('deal.contact.person')} entityLabel={t('deal.team.entity')} search={search} value={name} onSelect={(row) => { setEmploymentId(row.id); setName(row.line1); }} onAddNew={() => addNew.open({ kind: 'contact', companyId, pick: 'employment' }, (row) => { setEmploymentId(row.id); setName(row.line1); })} />
      </div>
      <Checkbox label={t('deal.contact.primary')} checked={isPrimary} size={Size.Small} onChange={setIsPrimary} />
      {addNew.host}
    </Modal>
  );
}

interface ReleaseAnswer {
  settings: Record<string, unknown>;
  offers: { companyId: string; selectable: boolean; reason: string; label: string }[];
  /** 🔒 Decision 8960: true on a Confidential file, whose release waits for approval; an answer without the key asks nobody. */
  needsApproval?: boolean;
}

/**
 * Release on a file row: the target list offered greyed with its reason, one FileRelease row per release, through piece 9's gate.
 * 🔒 Decision 11480: the one Release window on every Files list that offers Release, the Deal's, the Company's, the Location's,
 * the Bill's and the Purchase Order's, each handing it the companies it lists and the sentence it reads when there are none.
 */
export function ReleaseFileModal({ open, fileId, fileName, companies, customerCompanyId, noTargets = t('deal.release.noTargets'), apiBaseUrl = '', onCancel, onSaved }: { open: boolean; fileId: string | null; fileName: string | null; companies: readonly { id: string; name: string }[]; customerCompanyId: string | null; noTargets?: string; apiBaseUrl?: string; onCancel: () => void; onSaved: (message: string) => void }) {
  const ids = companies.map((c) => c.id).join(',');
  const targets = useApi<ReleaseAnswer>(open && fileId && ids ? `${FILES_PATH}/${fileId}/release-targets?companyIds=${encodeURIComponent(ids)}${customerCompanyId ? `&customerCompanyId=${encodeURIComponent(customerCompanyId)}` : ''}` : null, apiBaseUrl);
  const [toCompanyId, setToCompanyId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const offers = targets.state.kind === 'ready' ? targets.state.data.offers : [];
  const needsApproval = targets.state.kind === 'ready' && targets.state.data.needsApproval === true;
  const save = async () => {
    setError(null);
    if (!fileId || !toCompanyId) return setError(t('files.release.needsTarget'));
    const answer = await post<{ approvalStatus?: string }>(`${FILES_PATH}/${fileId}/release`, { toCompanyId, reason: reason.trim() || null }, apiBaseUrl);
    if (!answer.ok) {
      // 🔒 Decision 10340: a refusal naming the release owner, or the capability where nobody owns the file, is drawn here through t() from the rule, the name and the key it carries.
      const owner = releaseOwnerOf(answer.body);
      return setError(owner ? releaseOwnerSentence(owner) : answer.message);
    }
    // 🔒 Decision 8960: a Confidential file's release says it waits for approval, never that the file went.
    const company = companies.find((c) => c.id === toCompanyId)?.name ?? '';
    onSaved(answer.data.approvalStatus === 'Pending' ? line(t('files.release.sentForApproval'), company) : line(t('files.release.done'), company));
    onCancel();
  };
  return (
    <Modal open={open} title={line(t('files.release.title'), fileName ? `· ${fileName}` : null)} onCancel={onCancel} dataModal="release-file" actions={[{ kind: 'primary', label: t('files.release.action'), onAct: () => save() }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {companies.length === 0 ? <p className={styles.hint} data-release-no-targets="true">{noTargets}</p> : null}
      {/* 🔒 Decision 8960: before the press, the window says a Confidential file's release waits for an approver. */}
      {needsApproval ? <p className={styles.hint} data-release-needs-approval="true">{t('files.release.waitsForApproval')}</p> : null}
      <ul className={`${sharedStyles.listReset} ${targetStyles.targets}`} data-release-targets="true">
        {offers.map((offer) => {
          const company = companies.find((c) => c.id === offer.companyId);
          return (
            <li key={offer.companyId} data-release-target={offer.companyId} data-release-selectable={offer.selectable ? 'true' : 'false'}>
              <label className={styles.radio}>
                <input type="radio" name="releaseTo" value={offer.companyId} disabled={!offer.selectable} checked={toCompanyId === offer.companyId} onChange={() => setToCompanyId(offer.companyId)} />
                <span>{line(company?.name ?? offer.companyId, offer.selectable ? null : `· ${offer.label}`)}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {/* 🔒 The reason leads the approval's sentence, so a Confidential file's release marks it required (piece 9; T3303). */}
      <TextInput chars={32} label={t('files.release.reason')} value={reason} onChange={setReason} name="releaseReason" required={needsApproval} />
    </Modal>
  );
}
